import { useQuery } from '@tanstack/react-query'
import api from '../utils/api.js'

interface UsageNudgeResult {
  shouldNudge: boolean
  nearCapResources: string[]
}

/** Slice of the /billing/plan payload this hook reads. */
interface BillingPlanUsage {
  usage?: {
    users?: number | null
    items?: number | null
    venues?: number | null
    storage_mb?: number | null
  }
  limits?: {
    max_users?: number | null
    max_items?: number | null
    max_venues?: number | null
    max_storage_mb?: number | null
  }
}

/**
 * Returns { shouldNudge, nearCapResources } when any metered resource
 * is at or above 80% of its plan limit. Reads the same /billing/plan
 * endpoint already fetched by AppLayout — React Query deduplicates the
 * request so this adds zero extra network calls in normal use.
 */
export function useUsageNudge(): UsageNudgeResult {
  const { data } = useQuery({
    queryKey: ['billing-plan-usage'],
    queryFn: () => api.get<BillingPlanUsage>('/billing/plan').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })

  if (!data?.usage || !data?.limits) {
    return { shouldNudge: false, nearCapResources: [] }
  }

  const checks = [
    { used: data.usage.users,      max: data.limits.max_users,      label: 'users' },
    { used: data.usage.items,      max: data.limits.max_items,      label: 'items' },
    { used: data.usage.venues,     max: data.limits.max_venues,     label: 'venues' },
    { used: data.usage.storage_mb, max: data.limits.max_storage_mb, label: 'storage' },
  ]

  const nearCap = checks
    .filter(c => c.max != null && c.max > 0 && c.used != null && (c.used as number) / (c.max as number) >= 0.8)
    .map(c => c.label)

  return { shouldNudge: nearCap.length > 0, nearCapResources: nearCap }
}
